import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: 'Select a service...',
    message: ''
  });

  const [status, setStatus] = useState('idle'); // 'idle', 'submitting', 'success', 'error'
  const [responseMsg, setResponseMsg] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    // Combine UI fields to match the Laravel backend validation requirements
    const payload = {
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      phone: formData.phone,
      message: formData.service !== 'Select a service...'
        ? `Interested In: ${formData.service}\n\nProject Details:\n${formData.message}`
        : formData.message
    };

    try {
      const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/backend/api/contact`, payload, {
        headers: { 'Accept': 'application/json' }
      });

      setStatus('success');
      setResponseMsg(res.data.message);

      // Clear the form on success
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        service: 'Select a service...',
        message: ''
      });

    } catch (err) {
      setStatus('error');
      setResponseMsg(err.response?.data?.error || 'Something went wrong. Please try again.');
    }
  };

  return (
    <>
      <SEO
        title="Contact A2Z Signs | Get a Free Custom Signage Quote USA"
        description="Contact A2Z Signs today for a free custom signage quote. We provide monument signs, channel letters, window graphics"
        keyword="sign company consultation Upland California"
      />

      <div className="pt-32 pb-24 min-h-screen bg-brand-light">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl">

          <div className="text-center mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-heading font-bold text-brand-dark mb-4"
            >
              Get a <span className="text-brand-cyan">Free Quote</span>
            </motion.h1>
            <div className="w-24 h-1 bg-brand-cyan mx-auto rounded-full"></div>
            <p className="mt-6 text-gray-600 max-w-2xl mx-auto">
              Ready to elevate your brand's visibility? Fill out the form below or reach out to us directly to discuss your project.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">

            {/* Left Column: Contact Info */}
            <div className="bg-brand-dark text-white p-10 md:p-14 flex flex-col justify-between relative overflow-hidden">
              {/* Decorative background element */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-cyan opacity-10 rounded-full blur-[80px] -mr-20 -mt-20"></div>

              <div className="relative z-10">
                <h3 className="font-heading font-bold text-3xl mb-8">Contact Information</h3>

                <div className="space-y-8">
                  <div className="flex items-start gap-4 group">
                    <div className="p-3 bg-white/10 rounded-lg text-brand-cyan group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
                      <Phone size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-lg mb-1">Call Us</p>
                      <a href="tel:+19093053825" className="text-gray-300 hover:text-white transition-colors">
                        +1 (909) 305-3825
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="p-3 bg-white/10 rounded-lg text-brand-cyan group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
                      <Mail size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-lg mb-1">Email Us</p>
                      <a href="mailto:A2zsigns.co@gmail.com" className="text-gray-300 hover:text-white transition-colors">
                        A2zsigns.co@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="p-3 bg-white/10 rounded-lg text-brand-cyan group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-lg mb-1">Our Facility</p>
                      <p className="text-gray-300">1302 Monte Vista Ave #15<br />Upland, CA 91786</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="p-3 bg-white/10 rounded-lg text-brand-cyan group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
                      <Clock size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-lg mb-1">Business Hours</p>
                      <p className="text-gray-300">Mon - Fri: 8:00 AM - 5:00 PM<br />Sat - Sun: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="p-10 md:p-14">
              <h3 className="font-heading font-bold text-2xl text-brand-dark mb-8">Send Us a Message</h3>

              {/* Status Messages */}
              {status === 'success' && (
                <div className="mb-6 p-4 bg-green-50 text-green-700 font-bold rounded-xl border border-green-200">
                  {responseMsg}
                </div>
              )}

              {status === 'error' && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 font-bold rounded-xl border border-red-200">
                  {responseMsg}
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text" name="firstName" required value={formData.firstName} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-colors"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text" name="lastName" required value={formData.lastName} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-colors"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email" name="email" required value={formData.email} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel" name="phone" value={formData.phone} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-colors"
                      placeholder="(909) 305-3825"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Interested In (Optional)</label>
                  <select
                    name="service" value={formData.service} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-colors bg-white"
                  >
                    <option>Select a service...</option>
                    <option>Illuminated Signs</option>
                    <option>Monument Signs</option>
                    <option>Window Graphics</option>
                    <option>Custom Awnings</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Project Details *</label>
                  <textarea
                    name="message" required rows="4" value={formData.message} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-colors resize-none"
                    placeholder="Tell us about your signage needs..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-4 bg-brand-dark text-white font-bold rounded-lg hover:bg-brand-cyan hover:text-brand-dark transition-colors duration-300 flex justify-center items-center disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    "Submit Request"
                  )}
                </button>
              </form>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;